import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Row,
  Col,
  Divider,
  Tooltip,
  Typography,
  Statistic,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
  UserOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { clientAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';

const { Title, Text } = Typography;
const { Option } = Select;

interface Client {
  id: string;
  name: string;
  status: string;
  domain?: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

interface ClientStats {
  users: {
    total: number;
    active: number;
    invited: number;
    suspended: number;
    inactive: number;
  };
  roles: {
    admin: number;
    manager: number;
    user: number;
    analyst: number;
    viewer: number;
  };
  recent_logins: number;
  last_updated: string;
}

const ClientManagementPage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [statsModalVisible, setStatsModalVisible] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [selectedClientStats, setSelectedClientStats] = useState<ClientStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  const [form] = Form.useForm();

  const statusOptions = [
    { value: 'active', label: 'Active', color: 'green' },
    { value: 'suspended', label: 'Suspended', color: 'orange' },
    { value: 'inactive', label: 'Inactive', color: 'red' },
  ];

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await clientAPI.getClients();
      setClients(response.data.clients || []);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientStats = async (clientId: string) => {
    try {
      setStatsLoading(true);
      const response = await clientAPI.getClientStats(clientId);
      setSelectedClientStats(response.data.stats);
      setStatsModalVisible(true);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to fetch client stats');
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  // Only super admins can access this page - check after hooks
  if (user?.role !== 'super_admin') {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Card>
          <Title level={3}>Access Denied</Title>
          <Text>Only super administrators can access client management.</Text>
        </Card>
      </div>
    );
  }

  const handleCreateClient = async (values: any) => {
    try {
      await clientAPI.createClient(values);
      message.success('Client created successfully');
      setModalVisible(false);
      form.resetFields();
      fetchClients();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to create client');
    }
  };

  const handleUpdateClient = async (values: any) => {
    if (!editingClient) return;

    try {
      await clientAPI.updateClient(editingClient.id, values);
      message.success('Client updated successfully');
      setModalVisible(false);
      setEditingClient(null);
      form.resetFields();
      fetchClients();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update client');
    }
  };

  const openEditModal = (client: Client) => {
    setEditingClient(client);
    form.setFieldsValue({
      name: client.name,
      status: client.status,
      domain: client.domain,
      description: client.description,
    });
    setModalVisible(true);
  };

  const openCreateModal = () => {
    setEditingClient(null);
    form.resetFields();
    setModalVisible(true);
  };

  // Filter clients based on search text
  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchText.toLowerCase()) ||
    client.domain?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Client Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Client) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          {record.domain && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.domain}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusConfig = statusOptions.find(s => s.value === status);
        return (
          <Tag color={statusConfig?.color || 'default'}>
            {status.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => (
        <Text ellipsis style={{ maxWidth: 200 }}>
          {text || '-'}
        </Text>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Client) => (
        <Space>
          <Tooltip title="View Statistics">
            <Button
              type="text"
              icon={<EyeOutlined />}
              loading={statsLoading}
              onClick={() => fetchClientStats(record.id)}
            />
          </Tooltip>
          <Tooltip title="Edit Client">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Space align="center">
              <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate('/dashboard')}
                type="text"
                size="large"
              />
              <div>
                <Title level={3} style={{ margin: 0 }}>
                  Client Management
                </Title>
                <Text type="secondary">
                  Manage all client organizations and tenants
                </Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchClients}>
                Refresh
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreateModal}
              >
                Create Client
              </Button>
            </Space>
          </Col>
        </Row>

        <Divider />

        {/* Search */}
        <Row style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Input
              placeholder="Search clients by name or domain"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Col>
        </Row>

        <Table
          dataSource={filteredClients}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} clients`,
          }}
        />
      </Card>

      {/* Create/Edit Client Modal */}
      <Modal
        title={editingClient ? 'Edit Client' : 'Create New Client'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingClient(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={editingClient ? handleUpdateClient : handleCreateClient}
        >
          <Form.Item
            name="name"
            label="Client Name"
            rules={[
              { required: true, message: 'Please enter client name' },
              { min: 2, message: 'Name must be at least 2 characters' },
            ]}
          >
            <Input placeholder="Company Name" />
          </Form.Item>

          <Form.Item
            name="domain"
            label="Domain"
            rules={[
              { 
                pattern: /^[a-zA-Z0-9]([a-zA-Z0-9\-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9\-]*[a-zA-Z0-9])?)*$/,
                message: 'Please enter a valid domain' 
              },
            ]}
          >
            <Input placeholder="company.com" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea 
              placeholder="Brief description of the client organization"
              rows={3}
            />
          </Form.Item>

          {editingClient && (
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: 'Please select status' }]}
            >
              <Select placeholder="Select status">
                {statusOptions.map(status => (
                  <Option key={status.value} value={status.value}>
                    {status.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingClient ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Client Statistics Modal */}
      <Modal
        title="Client Statistics"
        open={statsModalVisible}
        onCancel={() => {
          setStatsModalVisible(false);
          setSelectedClientStats(null);
        }}
        footer={null}
        width={700}
      >
        {selectedClientStats && (
          <div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Total Users"
                    value={selectedClientStats.users.total}
                    prefix={<TeamOutlined />}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Active Users"
                    value={selectedClientStats.users.active}
                    prefix={<UserOutlined />}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Recent Logins"
                    value={selectedClientStats.recent_logins}
                    suffix="(30 days)"
                  />
                </Card>
              </Col>
            </Row>

            <Divider orientation="left">User Status Distribution</Divider>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Statistic
                  title="Active"
                  value={selectedClientStats.users.active}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Invited"
                  value={selectedClientStats.users.invited}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Suspended"
                  value={selectedClientStats.users.suspended}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Inactive"
                  value={selectedClientStats.users.inactive}
                  valueStyle={{ color: '#cf1322' }}
                />
              </Col>
            </Row>

            <Divider orientation="left">Role Distribution</Divider>
            <Row gutter={16}>
              <Col span={5}>
                <Statistic
                  title="Admins"
                  value={selectedClientStats.roles.admin}
                />
              </Col>
              <Col span={5}>
                <Statistic
                  title="Managers"
                  value={selectedClientStats.roles.manager}
                />
              </Col>
              <Col span={5}>
                <Statistic
                  title="Users"
                  value={selectedClientStats.roles.user}
                />
              </Col>
              <Col span={5}>
                <Statistic
                  title="Analysts"
                  value={selectedClientStats.roles.analyst}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="Viewers"
                  value={selectedClientStats.roles.viewer}
                />
              </Col>
            </Row>

            <Divider />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Last updated: {new Date(selectedClientStats.last_updated).toLocaleString()}
            </Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ClientManagementPage;
